import React from "react";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import Select from "react-select";

import Table from "../../../layout/table";
import { initialState } from "../../../layout/table/helpers";

import { get as getAccounts } from "../../../actions/accounts";
import { ledger as getLedgerLines } from "../../../actions/journal.entry.lines";
import { getAccountingLedger, saveAccountingLedger } from "../../../actions/staff";
import { send as pageInfo, loading } from "../../../actions/page";
import { format as formatDate } from "../../../utils/date";
import { currency as formatCurrency } from "../../../utils/number";

const accountLabel = (account) => {
  const number = String(account?.accountNumber || "").trim();
  const name = String(account?.name || "").trim();
  return [number, name].filter(Boolean).join(" - ") || account?.uuid || "";
};

class Ledger extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ...initialState({ order: "entryDate", direction: "DESC", size: 25 }),
      count: 0,
      accounts: [],
      accountUuids: [],
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Ledger", breadcrumb: "Ledger" });
    await this.loadPreferencesAndData();
  };

  loadPreferencesAndData = async () => {
    this.props.loading(true);

    const [accounts, preference] = await Promise.all([
      this.props.getAccounts(null),
      this.props.getAccountingLedger(),
    ]);

    const validAccountUuids = new Set((accounts || []).map((account) => account.uuid));
    const accountUuids = (preference?.accountUuids || []).filter((uuid) => validAccountUuids.has(uuid));

    this.setState({
      accounts: accounts || [],
      accountUuids,
      page: 0,
    }, this.loadData);
  };

  loadData = async () => {
    const { accountUuids, page, size, order, direction } = this.state;
    const result = await this.props.getLedgerLines({
      accountUuids,
      page,
      size,
      order,
      direction,
    });
    const count = Number(result?.count ?? 0);
    const pages = size > 0 ? Math.ceil(count / Number(size)) : 0;

    this.setState({
      data: result?.items || [],
      count,
      pages,
    });

    this.props.loading(false);
  };

  get accountOptions() {
    return (this.state.accounts || [])
      .filter((account) => account?.uuid && account.active !== false)
      .map((account) => ({
        value: account.uuid,
        label: accountLabel(account),
      }));
  }

  get selectedAccountOptions() {
    const selected = new Set(this.state.accountUuids || []);
    return this.accountOptions.filter((option) => selected.has(option.value));
  }

  get headers() {
    return [
      {
        header: "Date",
        sortable: true,
        source: "JournalEntry.entryDate",
        className: "text-muted text-small text-uppercase w-10",
        onRender: (_value, row) => this.renderEntryValue(formatDate(row?.JournalEntry?.entryDate), row),
      },
      {
        header: "Account",
        sortable: true,
        source: "Account.name",
        className: "text-muted text-small text-uppercase w-20",
        onRender: (_value, row) => this.renderAccount(row),
      },
      {
        header: "Reference",
        sortable: true,
        source: "JournalEntry.referenceNumber",
        className: "text-muted text-small text-uppercase w-15",
        onRender: (_value, row) => this.renderEntryValue(row?.JournalEntry?.referenceNumber || "", row),
      },
      {
        header: "Memo",
        sortable: false,
        source: "memo",
        className: "text-muted text-small text-uppercase w-40",
        onRender: (value, row) => this.renderMemo(value || row?.JournalEntry?.memo || "", row),
      },
      {
        header: "Debit",
        sortable: true,
        source: "debitAmount",
        className: "text-muted text-small text-uppercase w-10 text-end",
        dataClassName: "text-end",
        onRender: (value, row) => this.renderEntryValue(formatCurrency(Number(value || 0)), row),
      },
      {
        header: "Credit",
        sortable: true,
        source: "creditAmount",
        className: "text-muted text-small text-uppercase w-10 text-end",
        dataClassName: "text-end",
        onRender: (value, row) => this.renderEntryValue(formatCurrency(Number(value || 0)), row),
      },
    ];
  }

  isVoidedEntry = (row) => row?.JournalEntry?.status === "VOIDED";

  renderEntryValue = (text, row) => (
    <span className={this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : undefined}>
      {text}
    </span>
  );

  renderAccount = (row) => {
    const account = row?.Account || {};
    const label = accountLabel(account);
    return (
      <a
        href={`/dashboard/accounting/accounts/${account.uuid}`}
        className={this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : undefined}
      >
        {label}
      </a>
    );
  };

  renderStatusBadge = (row) => {
    const status = row?.JournalEntry?.status;
    if (!status) return null;

    const label = status.charAt(0) + status.slice(1).toLowerCase();
    const badge = status === "VOIDED"
      ? "bg-danger"
      : status === "POSTED"
        ? "bg-primary"
        : "bg-light text-dark";

    return <span className={`badge ${badge}`}>{label}</span>;
  };

  renderClearedBadge = (row) => {
    if (!row?.clearedAt) return "";

    const content = `Cleared ${formatDate(row.clearedAt)}`;
    const className = "badge bg-success";
    if (!row?.bankReconciliationUuid) return <span className={className}>{content}</span>;

    return (
      <a
        href={`/dashboard/accounting/reconciliations/${row.bankReconciliationUuid}`}
        className={className}
      >
        {content}
      </a>
    );
  };

  renderMemo = (text, row) => (
    <span className={this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : undefined}>
      {text}
      <span className="ms-2 d-inline-flex gap-1 align-middle">
        {this.renderStatusBadge(row)}
        {this.renderClearedBadge(row)}
      </span>
    </span>
  );

  renderCleared = (row) => {
    if (!row?.clearedAt) return "";

    const content = `✓ ${formatDate(row.clearedAt)}`;
    if (!row?.bankReconciliationUuid) return this.renderEntryValue(content, row);

    return (
      <a
        href={`/dashboard/accounting/reconciliations/${row.bankReconciliationUuid}`}
        className={this.isVoidedEntry(row) ? "text-decoration-line-through text-muted" : undefined}
      >
        {content}
      </a>
    );
  };

  onAccountsChange = async (options = []) => {
    const accountUuids = (options || []).map((option) => option.value);

    this.setState({ accountUuids, page: 0 }, async () => {
      await this.props.saveAccountingLedger({ accountUuids });
      this.props.loading(true);
      await this.loadData();
    });
  };

  onPageChange = async (page) => {
    this.setState({ page: Number(page) }, this.loadData);
  };

  onSize = async (size) => {
    this.setState({ size: Number(size), page: 0 }, this.loadData);
  };

  onColumnClick = async (header) => {
    if (!header?.sortable) return;

    const sourceMap = {
      "JournalEntry.entryDate": "entryDate",
      "JournalEntry.referenceNumber": "referenceNumber",
      "Account.name": "name",
    };
    const order = sourceMap[header.source] || header.source;
    const direction = this.state.order === order && this.state.direction === "ASC" ? "DESC" : "ASC";

    this.setState({ order, direction, page: 0 }, this.loadData);
  };

  render() {
    return (
      <>
        <Col xs="12">
          <Row className="align-items-end g-3">
            <Col xs="12" lg="8">
              <div className="mb-3 top-label">
                <Select
                  inputId="ledgerAccountUuids"
                  classNamePrefix="react-select"
                  isMulti
                  isClearable
                  placeholder="All accounts"
                  options={this.accountOptions}
                  value={this.selectedAccountOptions}
                  onChange={this.onAccountsChange}
                />
                <label>ACCOUNTS</label>
              </div>
            </Col>
            <Col xs="12" lg="4" className="text-muted small text-lg-end mb-3">
              {this.state.accountUuids.length ? `${this.state.accountUuids.length} selected` : "All accounts"} · {this.state.count} entries
            </Col>
          </Row>
        </Col>

        <Col xs="12">
          <Table
            headers={this.headers}
            page={this.state.data}
            size={this.state.size}
            index={this.state.page}
            pages={this.state.pages}
            onColumnClick={this.onColumnClick}
            onPageChange={this.onPageChange}
            onSize={this.onSize}
            searchable={false}
            sizable={true}
          />
        </Col>
      </>
    );
  }
}

Ledger.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  getAccounts,
  getLedgerLines,
  getAccountingLedger,
  saveAccountingLedger,
  pageInfo,
  loading,
})(Ledger);
